/**
 * As rotas do aplicativo.
 *
 * As rotas de negocio nao estao escritas aqui: sao construidas a partir dos
 * manifestos dos plugins, ja filtradas pelos escopos desta pessoa (regra 1 e
 * ADR-0001). Este arquivo nao pode conter o nome de nenhum dominio — ha um
 * teste que o verifica lendo o proprio codigo-fonte.
 *
 * A casca so traz o que e da plataforma: inicio, assistente, configuracoes,
 * diferencas por rever e entrada.
 */
import { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastProvider } from '@flos/ui';
import type { BootResult } from './boot';
import { AppProvider, useApp } from './state/AppContext';
import { AppShell } from './components/AppShell';
import { PluginRouteHost } from './components/PluginRouteHost';
import { HomeScreen } from './screens/HomeScreen';
import { AssistantScreen } from './screens/AssistantScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { ConflictScreen } from './screens/ConflictScreen';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { SHELL_PATHS } from './navigation';

function AppRoutes() {
  const { boot, session } = useApp();

  // A entrada deixa de mandar assim que houver sessao — sem reiniciar a app.
  if (boot.needsOnboarding && !session) {
    return (
      <Routes>
        <Route path={SHELL_PATHS.ONBOARDING} element={<OnboardingScreen />} />
        {/* Vai-se para a entrada, mas nada obriga a acabá-la. */}
        <Route path="*" element={<Navigate to={SHELL_PATHS.ONBOARDING} replace />} />
      </Routes>
    );
  }

  return <ShellRoutes />;
}

function ShellRoutes() {
  const { boot, session } = useApp();

  // Recalculado quando a sessao muda: o registro devolve so o que esta pessoa
  // pode abrir, portanto entrar ou sair muda mesmo o conjunto de rotas.
  const pluginRoutes = useMemo(
    () => boot.core.registry.routes(),
    [boot.core, session],
  );

  return (
    <Routes>
      <Route path={SHELL_PATHS.ONBOARDING} element={<OnboardingScreen />} />

      <Route element={<AppShell />}>
        <Route index element={<HomeScreen />} />
        <Route path={SHELL_PATHS.ASSISTANT} element={<AssistantScreen />} />
        <Route path={SHELL_PATHS.SETTINGS} element={<SettingsScreen />} />
        <Route path={SHELL_PATHS.CONFLICTS} element={<ConflictScreen />} />

        {pluginRoutes.map((route) => (
          <Route
            key={route.fullPath}
            path={route.fullPath}
            element={<PluginRouteHost route={route} />}
          />
        ))}

        <Route path="*" element={<NotFoundScreen />} />
      </Route>
    </Routes>
  );
}

export interface AppProps {
  boot: BootResult;
}

export function App({ boot }: AppProps) {
  return (
    <AppProvider boot={boot}>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AppProvider>
  );
}

export default App;
